import {
  Col,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  Card,
  Grid,
  Image,
  Descriptions,
  Tooltip,
} from "antd"
import { HiOutlineHandRaised } from "react-icons/hi2"
import useFetchTicketsHoldOrders from "../../store/Tickets/useFetchTicketHoldOrders"
import { ASAPOrder, OrderAndPrefix } from "../Orders/Helpers/OrderAndPrefix"
import moment from "moment"
import { generateFilter } from "../Orders/JobFilters"
import { returnProperMachineTag } from "../utils/ReturnProperMachineTag"
import PageHeader from "../Elements/PageHeader"

const { Text, Title, Paragraph } = Typography
const { useBreakpoint } = Grid

// A small helper to render data safely, showing a placeholder if data is missing.
const SafeText = ({ value, children, placeholder = "N/A" }) => (
  <Text>{value || children || placeholder}</Text>
)

// Mobile Card View: Unchanged, as it provides a good fallback for small screens.
const OrderCard = ({ order, rowClassName }) => (
  <Card
    className={rowClassName}
    style={{ marginBottom: "16px" }}
    title={
      <Space>
        <Title level={5} style={{ margin: 0 }}>
          {OrderAndPrefix(order)}
        </Title>
        {ASAPOrder(order)}
      </Space>
    }
    extra={<DueDateTag date={order.orderDueDate} />}>
    {order.reportComment && (
      <Card type="inner" size="small" style={{ marginBottom: 12, backgroundColor: "#FFFBE6" }}>
        <Paragraph ellipsis={{ rows: 2, expandable: true, symbol: "more" }} style={{ margin: 0 }}>
          <Text strong>Report Comment: </Text>
          {order.reportComment}
        </Paragraph>
      </Card>
    )}
    <Descriptions bordered size="small" column={1}>
      <Descriptions.Item label="Client">{order.client?.client}</Descriptions.Item>
      <Descriptions.Item label="Product">
        {order.selectedProduct?.product?.productName}
      </Descriptions.Item>
      <Descriptions.Item label="Machine">
        {returnProperMachineTag(order.selectedProduct?.machine)}
      </Descriptions.Item>
      <Descriptions.Item label="Sales Manager">
        {order.userData?.salesEmail || "N/A"}
      </Descriptions.Item>
      <Descriptions.Item label="Designer">
        {order.userData?.designerEmail || "N/A"}
      </Descriptions.Item>
      <Descriptions.Item label="Req. Amount">{order.labelCount || "N/A"}</Descriptions.Item>
      <Descriptions.Item label="Weight">
        {order.weight ? `${order.weight} kg` : "N/A"}
      </Descriptions.Item>
    </Descriptions>
  </Card>
)

// Helper for the Due Date Tag (using your original logic)
const DueDateTag = ({ date }) => {
  if (!date?.seconds) return null
  const dueDate = moment.unix(date.seconds)
  const isOverdue = dueDate.isBefore(moment(), "day")

  const color = isOverdue ? "red" : "blue"
  return <Tag color={color}>{dueDate.format("DD.MM.YYYY")}</Tag>
}

// --- UPDATED PRINT SPECIFICATIONS COMPONENT ---
// Now includes all requested fields and filters out "Percent" keys.
const PrintSpecifications = ({ printData }) => {
  if (!printData) return null

  const labelMap = {
    cmykColors: "CMYK Colors",
    pantoneColors: "Pantone Colors",
    varnishType: "Varnish Type",
    varnishId: "Varnish ID",
    varnishUse: "Varnish Use",
    foilColor: "Foil Color",
    foilId: "Foil ID",
    foilCount: "Foil Count",
    relief: "Relief",
    reliefId: "Relief ID",
    goldOrSilver: "Gold/Silver",
    coveringWCount: "Covering White Count",
  }

  const items = Object.entries(printData)
    .map(([key, value]) => {
      // Exclude keys containing "Percent" and only show items with a meaningful value
      if (!key.includes("Percent") && labelMap[key] && value && value !== "none" && value !== 0) {
        return {
          key,
          label: labelMap[key],
          children: <SafeText value={value} />,
        }
      }
      return null
    })
    .filter(Boolean) // Remove null entries

  if (items.length === 0) {
    return <Text type="secondary">No specific print details provided.</Text>
  }

  return <Descriptions bordered column={2} size="small" items={items} />
}

export default function TicketHoldOrders({ forManager = false }) {
  const { ticketsOrders: holdOrders, loading: holdOrdersLoading } = useFetchTicketsHoldOrders()
  const screens = useBreakpoint()

  const clientFilters = generateFilter(holdOrders, ["client", "client"])
  const productFilter = generateFilter(holdOrders, ["selectedProduct", "product", "productName"])
  const machineFilter = generateFilter(holdOrders, ["selectedProduct", "machine"])
  const materialFilter = generateFilter(holdOrders, ["selectedProduct", "material"])

  // --- UPDATED EXPANDED ROW RENDER ---
  // Added Technical Image to Production Details
  const expandedRowRender = (record) => {
    const allComments = [
      record.reportComment,
      record.salesComment,
      record.paperManagerComment,
      record.machineCompletion?.machineIssues?.issueComment,
    ].filter(Boolean)

    // Safely get the technical image URL from one of two possible locations
    const technicalImageUrl =
      record.technicalPage || record.selectedProduct?.product?.productFileUrl

    return (
      <div style={{ padding: "8px 16px", backgroundColor: "#fafafa" }}>
        {allComments.length > 0 && (
          <>
            <Title level={5}>Comments</Title>
            <Descriptions bordered column={1} size="small" style={{ marginBottom: 24 }}>
              {record.reportComment && (
                <Descriptions.Item label="Report Comment">{record.reportComment}</Descriptions.Item>
              )}
              {record.salesComment && (
                <Descriptions.Item label="Sales Comment">{record.salesComment}</Descriptions.Item>
              )}
              {record.paperManagerComment && (
                <Descriptions.Item label="Paper Manager Comment">
                  {record.paperManagerComment}
                </Descriptions.Item>
              )}
              {record.machineCompletion?.machineIssues?.issue && (
                <Descriptions.Item label="Machine Issue Comment">
                  {record.machineCompletion.machineIssues.issueComment}
                </Descriptions.Item>
              )}
            </Descriptions>
          </>
        )}

        <Title level={5}>Production Details</Title>
        <Descriptions bordered column={2} size="small" style={{ marginBottom: 24 }}>
          <Descriptions.Item label="Knife Code">
            <SafeText value={record.selectedProduct?.knifeCode} />
          </Descriptions.Item>
          <Descriptions.Item label="Used Meters">
            {record.machineCompletion?.usedMeters || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Order Weight">
            {record.weight ? `${record.weight} kg` : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Original Machine">
            {record.originalMachine || "N/A"}
          </Descriptions.Item>
          {/* Technical Image is added here */}
          {technicalImageUrl && (
            <Descriptions.Item label="Technical Image" span={2}>
              <Image src={technicalImageUrl} width={100} />
            </Descriptions.Item>
          )}
        </Descriptions>

        <Title level={5}>Personnel</Title>
        <Descriptions bordered column={2} size="small" style={{ marginBottom: 24 }}>
          <Descriptions.Item label="Sales Manager">
            <SafeText value={record.userData?.salesEmail} />
          </Descriptions.Item>
          <Descriptions.Item label="Designer">
            <SafeText value={record.userData?.designerEmail} />
          </Descriptions.Item>
          <Descriptions.Item label="Sent From">
            <SafeText value={record.fromUser} />
          </Descriptions.Item>
          <Descriptions.Item label="Last Updated By">
            <SafeText value={record.updatedByEmail} />
          </Descriptions.Item>
        </Descriptions>

        <Title level={5}>Print Specifications</Title>
        <PrintSpecifications printData={record.selectedProduct?.print} />
      </div>
    )
  }

  // --- RESTORED & ENHANCED TABLE COLUMNS ---
  // Added a new "Tech." column for the image
  const detailedColumns = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "left",
      sorter: (a, b) => a.orderNumber - b.orderNumber,
      render: (value, record) => (
        <Space>
          {ASAPOrder(record)}
          {OrderAndPrefix(record)}
        </Space>
      ),
      width: "10%",
    },

    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "right",
      filters: clientFilters,
      onFilter: (value, record) => record.client?.client?.startsWith(value),
      sorter: (a, b) => (a.client?.client || "").localeCompare(b.client?.client || ""),
      ellipsis: true,
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "left",
      filters: productFilter,
      onFilter: (value, record) => record.selectedProduct?.product?.productName?.startsWith(value),
      ellipsis: true,
      width: "20%",
    },
    {
      title: "Manager",
      dataIndex: "user",
      align: "center",
      width: "10%",
      render: (user) => {
        if (!user || typeof user !== "string") return "—"
        const name = user.split("@")[0]
        return (
          <Tooltip title={user}>
            <Text>{name}</Text>
          </Tooltip>
        )
      },
    },
    {
      title: "Manuf.",
      dataIndex: ["manufacturer", "company"],
      align: "center",
      ellipsis: true,
      render: (companyName) => {
        if (!companyName || typeof companyName !== "string") return "—"
        const initials = companyName
          .split(" ")
          .filter(Boolean)
          .map((word) => word[0])
          .slice(0, 2)
          .join("")
          .toUpperCase()
        return (
          <Tooltip title={companyName}>
            <Text>{initials}</Text>
          </Tooltip>
        )
      },
    },
    {
      title: "Machine",
      dataIndex: ["selectedProduct", "machine"],
      align: "center",
      filters: machineFilter,
      onFilter: (value, record) => record.selectedProduct?.machine?.startsWith(value),
      render: (value) => returnProperMachineTag(value),
    },
    {
      title: "Material",
      dataIndex: ["selectedProduct", "material"],
      align: "center",
      filters: materialFilter,
      onFilter: (value, record) => record.selectedProduct?.material?.startsWith(value),
      width: "10%",
    },
    {
      title: "Proj. M.",
      dataIndex: "projectedMeters",
      align: "center",
      width: "10%",
      sorter: (a, b) => a.projectedMeters - b.projectedMeters,
      render: (value) => <Text>{value ? `${value}m` : "0m"}</Text>,
    },
    {
      title: "M.Width",
      dataIndex: ["selectedProduct", "formatWidth"],
      align: "center",
      sorter: (a, b) =>
        (a.selectedProduct?.formatWidth || 0) - (b.selectedProduct?.formatWidth || 0),
      render: (value, record) => <Text>{record.readRollWidth ? record.rollWidth : value}</Text>,
    },
    {
      title: "Due D.",
      dataIndex: "orderDueDate",
      align: "center",
      sorter: (a, b) => (a.orderDueDate?.seconds || 0) - (b.orderDueDate?.seconds || 0),
      render: (value) => <DueDateTag date={value} />,
    },
    // New column for the technical image
    {
      title: "Tech.",
      dataIndex: "technicalPage",
      align: "center",
      render: (text, record) => {
        const imageUrl = record.technicalPage || record.selectedProduct?.product?.productFileUrl
        if (imageUrl) {
          return <Image src={imageUrl} width={25} preview={{ src: imageUrl }} />
        }
        return "—"
      },
    },
  ]

  return (
    <div style={{ padding: "16px" }}>
      <Row gutter={[16, 16]}>
        {forManager ? (
          ""
        ) : (
          <Col span={24}>
            <PageHeader icon={<HiOutlineHandRaised />} text="Hold Orders" />
          </Col>
        )}
        <Col span={24}>
          {screens.md ? (
            <Table
              bordered
              size="small"
              dataSource={holdOrders}
              loading={holdOrdersLoading}
              columns={detailedColumns}
              rowKey={(record) => record.id}
              rowClassName={(record) => (record.priorityOrder ? "red-background" : "")}
              pagination={{ pageSize: 50 }}
              expandable={{ expandedRowRender, expandRowByClick: true }}
              scroll={{ x: "max-content" }}
            />
          ) : (
            holdOrders?.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                rowClassName={order.priorityOrder ? "red-background" : ""}
              />
            ))
          )}
        </Col>
      </Row>
    </div>
  )
}
